// Microsoft Clarity configuration for KlaraView marketing-site visitor analytics.
//
// Scope: anonymous visitors on the public marketing site (klaraview.com).
// Clarity captures visits, traffic sources, geo/device, heatmaps and session
// replays. In-app (logged-in) usage is tracked separately by Application
// Insights — see config/appInsights.ts.
//
// GDPR: Clarity uses cookies, so it is only initialized AFTER the visitor
// grants analytics consent. No connection / no tracking happens before that.

import Clarity from '@microsoft/clarity';

const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || '';

export const CONSENT_STORAGE_KEY = 'klaraview.analyticsConsent';

let initialized = false;

/** Has the visitor already made a consent choice this session/device? */
export const getStoredConsent = (): 'granted' | 'denied' | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : null;
};

/**
 * Initialize Clarity. No-op when no project ID is configured or already
 * initialized. Should only be called once consent is granted.
 */
const initClarity = (): void => {
  if (initialized || !CLARITY_ID || typeof window === 'undefined') {
    return;
  }
  Clarity.init(CLARITY_ID);
  Clarity.consent(true);
  initialized = true;
};

/** Grant analytics consent: persist the choice and start Clarity. */
export const grantAnalyticsConsent = (): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'granted');
  }
  initClarity();
};

/** Deny analytics consent: persist the choice; Clarity is never started. */
export const denyAnalyticsConsent = (): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'denied');
  }
  if (initialized) {
    Clarity.consent(false);
  }
};

/**
 * On app load, re-apply a previously granted consent so returning visitors
 * are tracked without re-prompting. Does nothing if consent was denied or
 * never given.
 */
export const initClarityIfConsented = (): void => {
  if (getStoredConsent() === 'granted') {
    initClarity();
  }
};
